// English translations for the order detail page (French string -> English).
// Keys already present in the shared dictionary (common.ts) are NOT repeated
// here: Enregistrer, Annuler, Fermer, Supprimer, Modifier, Ajouter, Confirmer,
// Retour, Voir, Copier, Copié ✓, Rechercher, Terminé, Tout, Date, Statut,
// Client, Prix, Total, Solo/Duo/Trio, and the order statuses.
const m: Record<string, string> = {
  // ── PDF status badge ──────────────────────────────────────────────────────
  'Non généré': 'Not generated',
  'Génération en cours...': 'Generating…',
  'PDFs prêts': 'PDFs ready',
  'Erreur': 'Error',
  '⏱ Nos serveurs génèrent les PDFs en arrière-plan. Le client n\'a pas besoin de rester connecté — un contrôle automatique relance la génération si elle bloque.':
    '⏱ Our servers are generating the PDFs in the background. The customer doesn\'t need to stay online — an automatic check re-runs generation if it stalls.',
  'La génération serveur a échoué. Cliquez « Régénérer » ci-dessous pour relancer.':
    'Server generation failed. Click "Regenerate" below to retry.',
  'Les PDFs sont générés en pleine qualité par nos serveurs, en arrière-plan, dès la soumission — sans dépendre du navigateur du client. Pour ajuster: ouvrez l\'éditeur, modifiez, puis « Régénérer ».':
    'PDFs are generated at full quality by our servers, in the background, as soon as the album is submitted — without depending on the customer\'s browser. To adjust: open the editor, edit, then "Regenerate".',

  // ── Copy / open ───────────────────────────────────────────────────────────
  'Copier le lien': 'Copy link',
  '✓ Copié': '✓ Copied',
  '📋 Copier': '📋 Copy',
  'Ouvrir': 'Open',
  'Ouvrir ↗': 'Open ↗',

  // ── Spine info ────────────────────────────────────────────────────────────
  'Dos': 'Spine',
  '{n} pages': '{n} pages',
  'feuille': 'sheet',
  'feuilles': 'sheets',
  'papier {label}': '{label} paper',
  'Pages imprimées : {n} (multiple de 4 pour impression A3)': 'Printed pages: {n} (multiple of 4 for A3 printing)',

  // ── Fiche de montage / cover preview ──────────────────────────────────────
  'Télécharger la fiche de montage universelle (positionnement des cartons rigides). Une seule, réutilisable pour tous les albums — ses traits correspondent aux traits imprimés sur la couverture.': 'Download the universal assembly sheet (rigid board placement). A single, reusable sheet for every album — its marks match the marks printed on the cover.',
  'Génération…': 'Generating…',
  'Fiche de montage': 'Assembly sheet',
  "Aucune mise en page pour l'album {n} — le client n'a pas encore composé cet album.": 'No layout for album {n} — the customer has not composed this album yet.',
  "Échec de la génération de l'aperçu couverture. Voir la console.": 'Failed to generate the cover preview. See the console.',
  'Aperçu couv. A{n}': 'Cover preview A{n}',
  'Aperçu couverture': 'Cover preview',
  'Génère et ouvre la couverture A3+ (avec repères de montage) SANS soumettre la commande ni changer son statut — aperçu uniquement.': 'Generates and opens the A3+ cover (with assembly marks) WITHOUT submitting the order or changing its status — preview only.',

  // ── PDF generation card ───────────────────────────────────────────────────
  'Génération PDF': 'PDF generation',
  'Couverture A3+ (dos + tranche + couverture) + Intérieur + Livre complet': 'A3+ cover (spine + edge + cover) + Interior + Full book',
  'Couverture A3+ + Intérieur mis en page': 'A3+ cover + laid-out interior',
  '✓ Version finale validée le {date} — ces fichiers reflètent la dernière soumission du client': '✓ Final version validated on {date} — these files reflect the customer’s latest submission',
  "Ouvre l'éditeur sur l'album {n} — clique sur Confirmer pour régénérer tous les PDFs de cette commande (covers + intérieurs).": 'Opens the editor on album {n} — click Confirm to regenerate all PDFs for this order (covers + interiors).',
  'Régénérer': 'Regenerate',
  '⏱ Le client est en train de générer ses PDFs dans son navigateur...': '⏱ The customer is generating their PDFs in their browser…',
  "La génération a échoué côté client. Demandez-lui de re-soumettre l'album.": 'Generation failed on the customer’s side. Ask them to resubmit the album.',
  '⚠ Les PDFs ci-dessous sont antérieurs à la dernière modification du client.': '⚠ The PDFs below predate the customer’s latest edit.',
  'Dernière modification : {mod} — PDFs générés : {gen}.': 'Last edit: {mod} — PDFs generated: {gen}.',
  "Pour les régénérer : ouvrir l'éditeur, cliquer sur Commander.": 'To regenerate them: open the editor and click Order.',
  'Aperçu non destructif — ne soumet pas, ne change pas le statut.': 'Non-destructive preview — does not submit, does not change the status.',
  'Album {n}': 'Album {n}',
  'Couverture A3+ (dos + tranche + couv. avant)': 'A3+ cover (spine + edge + front cover)',
  'Intérieur PDF': 'Interior PDF',
  'Livre complet PDF': 'Full book PDF',
  "Les PDFs sont générés en pleine qualité par le navigateur du client lors de la soumission. Pour regénérer ou ajuster: ouvrez l'éditeur, modifiez, puis re-soumettez.": 'PDFs are generated at full quality by the customer’s browser on submission. To regenerate or adjust: open the editor, edit, then resubmit.',
  '✓ PDFs disponibles': '✓ PDFs available',
  'Dernière génération échouée — demandez au client de re-soumettre': 'Last generation failed — ask the customer to resubmit',
  'Production PDF': 'PDF production',
  "Disponible dès que l'album est soumis — ou ouvrez l'éditeur et cliquez Commander pour générer les PDF vous-même.": 'Available as soon as the album is submitted — or open the editor and click Order to generate the PDFs yourself.',

  // ── Preview / flipbook card ───────────────────────────────────────────────
  'Prêt à imprimer': 'Ready to print',
  'Modifications demandées': 'Changes requested',
  'En attente du client': 'Awaiting customer',
  'Aperçu client — flipbook 3D': 'Customer preview — 3D flipbook',
  'Le client feuillette son album, laisse ses notes page par page et confirme avant impression.': 'The customer flips through their album, leaves notes page by page, and confirms before printing.',
  'Album {n} — téléchargement des PDFs…': 'Album {n} — downloading PDFs…',
  "Album {n} : PDF inaccessible — régénérez d'abord les PDFs.": 'Album {n}: PDF unreachable — regenerate the PDFs first.',
  '✓ Aperçus générés ({n} albums)': '✓ Previews generated ({n} albums)',
  'Génération des aperçus échouée': 'Preview generation failed',
  "Aucun PDF disponible — régénérez d'abord les PDFs.": 'No PDF available — regenerate the PDFs first.',
  'Re-rasterise les aperçus depuis les PDFs stockés (dans ce navigateur).': 'Re-rasterizes the previews from the stored PDFs (in this browser).',
  'Générer les aperçus': 'Generate previews',
  'Révoquer': 'Revoke',
  "Générer le lien d'aperçu": 'Generate preview link',
  'Disponible une fois les PDFs générés.': 'Available once the PDFs are generated.',
  'Notes du client ({n})': 'Customer notes ({n})',
  "Marque toutes les notes comme corrigées et repasse l'aperçu en attente — le client pourra re-confirmer.": 'Marks all notes as fixed and sets the preview back to pending — the customer can re-confirm.',
  'Marquer comme corrigées': 'Mark as fixed',
  'Album {a} — Page {p} :': 'Album {a} — Page {p}:',
  '✓ {n} note corrigée (historique conservé).': '✓ {n} note fixed (history kept).',
  '✓ {n} notes corrigées (historique conservé).': '✓ {n} notes fixed (history kept).',
  "Les aperçus sont générés à partir des PDF d'impression. Générez d'abord les PDF (étape 3).": 'Previews are generated from the print PDFs. Generate the PDFs first (step 3).',

  // ── Redo / réimpression ───────────────────────────────────────────────────
  'Album complet (couv. + intérieur)': 'Full album (cover + interior)',
  'Couverture uniquement': 'Cover only',
  'Intérieur uniquement': 'Interior only',
  'Pages spécifiques': 'Specific pages',
  "Erreur d'impression": 'Print error',
  'Demande client': 'Customer request',
  'Problème de qualité': 'Quality issue',
  'Autre': 'Other',
  'Génération… redo': 'Generating…',
  'Prêt': 'Ready',
  "Envoyé à l'imprimeur": 'Sent to printer',
  'Redo / Réimpression': 'Redo / Reprint',
  'Réimprimer tout ou partie de la commande. Le coût (papier + encre) est un suivi interne — jamais facturé au client.': 'Reprint all or part of the order. The cost (paper + ink) is internal tracking — never charged to the customer.',
  'Album': 'Album',
  'Portée': 'Scope',
  'Pages à réimprimer': 'Pages to reprint',
  'Chargement des pages…': 'Loading pages…',
  'Raison': 'Reason',
  'Notes (optionnel)': 'Notes (optional)',
  'Détails internes…': 'Internal details…',
  'Coût estimé :': 'Estimated cost:',
  '— suivi interne': '— internal tracking',
  'Créer le redo': 'Create redo',
  'Sélectionnez au moins une page pour un redo "Pages spécifiques".': 'Select at least one page for a "Specific pages" redo.',
  'Création du redo…': 'Creating redo…',
  'Génération des pages (navigateur)…': 'Generating pages (browser)…',
  'Génération des PDF…': 'Generating PDFs…',
  'Préparation des fichiers…': 'Preparing files…',
  "L'album n'a pas encore de PDF généré. Régénérez d'abord l'album, puis créez le redo.": 'The album does not have a generated PDF yet. Regenerate the album first, then create the redo.',
  'Enregistrement…': 'Saving…',
  'Échec de la création du redo.': 'Failed to create the redo.',
  'Échec de la finalisation.': 'Failed to finalize.',
  'Historique des redos': 'Redo history',
  'Aucun redo pour cette commande.': 'No redo for this order.',
  'Raison :': 'Reason:',
  'pages {list}': 'pages {list}',
  'Coût interne : {n} MAD': 'Internal cost: {n} MAD',
  "Envoyer à l'imprimeur": 'Send to printer',
  'Marquer terminé': 'Mark done',

  // ── Redo printer-link modal ───────────────────────────────────────────────
  'album complet': 'full album',
  'couverture uniquement': 'cover only',
  'intérieur complet': 'full interior',
  'Ce redo n’a aucun PDF à envoyer. Générez d’abord les PDF.': 'This redo has no PDF to send. Generate the PDFs first.',
  'Échec de la création du lien.': 'Failed to create the link.',
  'Lien imprimeur (réimpression)': 'Printer link (reprint)',
  'Redo': 'Redo',
  'album {n}': 'album {n}',
  'fichier PDF.': 'PDF file.',
  'fichiers PDF.': 'PDF files.',
  'Le lien (valable 7 jours, sans connexion) ouvre la même page de téléchargement que les commandes groupées.': 'The link (valid 7 days, no login) opens the same download page as batched orders.',
  'Générer le lien': 'Generate link',
  'Lien prêt — le redo est marqué': 'Link ready — the redo is marked',
  "Lien à envoyer à l'imprimeur (WhatsApp / email) — valable 7 jours, sans connexion :": 'Link to send to the printer (WhatsApp / email) — valid 7 days, no login:',
  'Copié ✓': 'Copied ✓',
  'Ouvrir la page de téléchargement →': 'Open the download page →',

  // ── Pipeline rail + main page ─────────────────────────────────────────────
  'Aller à {label}': 'Go to {label}',
  'Photos': 'Photos',
  'PDF': 'PDF',
  'Aperçu': 'Preview',
  'Livraison': 'Delivery',
  'Relance': 'Follow-up',
  'Commande introuvable': 'Order not found',
  '← Retour': '← Back',
  'Virement': 'Bank transfer',
  'Mettre à jour': 'Update',
  '✓ Statut synchronisé avec Shopify': '✓ Status synced with Shopify',
  '✓ Album soumis le {date}': '✓ Album submitted on {date}',
  'Relance WhatsApp': 'WhatsApp follow-up',
  'Aucun numéro de téléphone sur cette commande.': 'No phone number on this order.',

  // ── Order costs card ──────────────────────────────────────────────────────
  'Matières (stock)': 'Materials (stock)',
  'Intérieur (imprimeur)': 'Interior (printer)',
  'Couverture (imprimeur)': 'Cover (printer)',
  '💰 Coût de revient': '💰 Cost price',
  "Les coûts apparaissent au fil de la production : matières à l'« Imprimée », impression au lot imprimeur, livraison à la livraison.": 'Costs appear as production progresses: materials at "Printed", printing at the printer batch, delivery at delivery.',
  'à venir': 'pending',
  'Coût total': 'Total cost',
  'Marge': 'Margin',
  '(sur {revenue})': '(on {revenue})',

  // ── Order profit card ─────────────────────────────────────────────────────
  '📈 Rentabilité': '📈 Profitability',
  'Calcul du bénéfice…': 'Computing profit…',

  // ── Danger zone (collapsible) ─────────────────────────────────────────────
  'Zone de danger': 'Danger zone',

  // ── Add album card ────────────────────────────────────────────────────────
  '➕ Ajouter un album': '➕ Add an album',
  "Crée un nouvel album sur cette commande (deviendra l'album {n}) et génère un lien client pour continuer l'envoi des photos.": 'Creates a new album on this order (will become album {n}) and generates a customer link to continue uploading photos.',
  'Ajouter un album': 'Add an album',
  'Créer un album': 'Create an album',
  'Commande': 'Order',
  "deviendra l'album {n}": 'will become album {n}',
  'Destination': 'Destination',
  'ex. Marrakech': 'e.g. Marrakech',
  'Année': 'Year',
  'ex. 2026': 'e.g. 2026',
  'Nombre de pages': 'Number of pages',
  "Prix de l'album (MAD)": 'Album price (MAD)',
  'ex. 349': 'e.g. 349',
  'Couverture': 'Cover',
  'Couverture sélectionnée': 'Cover selected',
  'Vierge / sans couverture': 'Blank / no cover',
  'Rechercher une couverture…': 'Search for a cover…',
  'Effacer': 'Clear',
  'Vierge': 'Blank',
  'Aucune couverture pour « {q} »': 'No cover for "{q}"',
  'Entrez un prix valide (≥ 0) pour le nouvel album.': 'Enter a valid price (≥ 0) for the new album.',
  "Échec de l'ajout de l'album.": 'Failed to add the album.',
  '✓ Album {n} ajouté': '✓ Album {n} added',
  'Prix ajouté à la commande :': 'Price added to the order:',
  'Lien client — à partager (continue dans le flux normal)': 'Customer link — to share (continues in the normal flow)',
  "Créer l'album + lien client": 'Create the album + customer link',
  'Partager sur WhatsApp': 'Share on WhatsApp',
  'Aucun numéro — copiez le lien.': 'No number — copy the link.',
  'Copié ✓ addalbum': 'Copied ✓',

  // ── Admin edit album card ─────────────────────────────────────────────────
  "Édition de l'album": 'Album editing',
  "Ouvrez l'éditeur pour téléverser les photos, choisir une couverture et organiser la mise en page": 'Open the editor to upload photos, choose a cover, and arrange the layout',
  'Destination non définie': 'Destination not set',
  "Ouvrir l'éditeur →": 'Open the editor →',

  // ── Editor unlock control ─────────────────────────────────────────────────
  'Lien éditeur :': 'Editor link:',
  'Éditeur déverrouillé pour le client': 'Editor unlocked for the customer',
  'Éditeur verrouillé (album soumis)': 'Editor locked (album submitted)',
  'Verrouiller': 'Lock',
  'Déverrouiller': 'Unlock',
  "Fenêtre d'upload ouverte jusqu'au {date}": 'Upload window open until {date}',
  'Autoriser le client à ajouter des photos (48h)': 'Allow the customer to add photos (48h)',
  '✓ Fenêtre ouverte': '✓ Window open',
  'Prolonger 48h': 'Extend 48h',
  'Partager pour upload': 'Share for upload',

  // ── Upload link card ──────────────────────────────────────────────────────
  "Lien d'upload client": 'Customer upload link',
  'Partagez ce lien pour que le client envoie ses photos sans se connecter.': 'Share this link so the customer can send their photos without logging in.',
  'Actif': 'Active',
  'Révoqué': 'Revoked',
  'Expiré': 'Expired',
  'Régénérer (nouveau lien)': 'Regenerate (new link)',
  'Confirmer la révocation': 'Confirm revocation',
  'Valide jusqu\'au {date}': 'Valid until {date}',
  'Premier upload : {date}': 'First upload: {date}',
  'Dernier upload : {date}': 'Last upload: {date}',
  'Générer un nouveau lien': 'Generate a new link',
  "Générer un lien d'upload": 'Generate an upload link',

  // ── Client photos card ────────────────────────────────────────────────────
  'Photos client': 'Customer photos',
  "Photos téléversées par le client via le lien d'upload.": 'Photos uploaded by the customer via the upload link.',
  '{n} au total': '{n} total',
  'Erreur de chargement :': 'Loading error:',
  'Aucun album.': 'No album.',
  '{n} photos': '{n} photos',
  '{n} photo': '{n} photo',
  'Aucune photo encore.': 'No photo yet.',
  'Éditeur Storiz': 'Storiz editor',
  'Lien client': 'Customer link',
  'Supprimer définitivement les {n} photos ?': 'Permanently delete the {n} photos?',
  'Oui, supprimer': 'Yes, delete',
  '🔄 Conversion en cours...': '🔄 Converting…',
  '🔄 Convertir les HEIC en JPEG': '🔄 Convert HEIC to JPEG',
  '✓ {done}/{total} converties': '✓ {done}/{total} converted',
  '🗑 Supprimer toutes les photos': '🗑 Delete all photos',

  // ── Order info card ───────────────────────────────────────────────────────
  'Informations commande': 'Order information',
  '✎ Modifier': '✎ Edit',
  'Nom du client': 'Customer name',
  'WhatsApp': 'WhatsApp',
  'Email': 'Email',
  'Note client': 'Customer note',
  'Adresse, remarques…': 'Address, remarks…',
  "Échec de l'enregistrement :": 'Save failed:',
  'erreur inconnue': 'unknown error',
  'Enregistré et synchronisé avec Shopify': 'Saved and synced with Shopify',
  'Enregistré localement — échec sync Shopify': 'Saved locally — Shopify sync failed',
  'N° commande': 'Order no.',
  'Formule': 'Package',
  'Pages': 'Pages',
  '({base} + {extra} page(s) suppl. × {rate})': '({base} + {extra} extra page(s) × {rate})',
  'Destination 1': 'Destination 1',
  'Destination 2': 'Destination 2',
  'Destination 3': 'Destination 3',

  // ── Danger zone card ──────────────────────────────────────────────────────
  'Supprimer cette commande retire ses photos, PDFs, colis AMEEX et la commande Shopify associée. Action irréversible.': 'Deleting this order removes its photos, PDFs, AMEEX parcel, and the associated Shopify order. Irreversible action.',
  'Supprimer la commande': 'Delete order',
  'Vous êtes sur le point de supprimer définitivement :': 'You are about to permanently delete:',
  '{n} page(s)': '{n} page(s)',
  'Les fichiers PDF générés': 'The generated PDF files',
  'Toutes les photos uploadées par le client': 'All photos uploaded by the customer',
  'Le colis AMEEX': 'The AMEEX parcel',
  '(annulé chez AMEEX)': '(cancelled at AMEEX)',
  'Synchroniser avec Shopify (supprime le brouillon ou annule la commande)': 'Sync with Shopify (deletes the draft or cancels the order)',
  'Tapez': 'Type',
  'pour confirmer': 'to confirm',
  'Supprimer définitivement': 'Delete permanently',

  // ── Smart follow-up card ──────────────────────────────────────────────────
  'Calcul du message…': 'Computing the message…',
  'Impossible de calculer le message de relance.': 'Unable to compute the follow-up message.',
  '💎 Relance 3D — album prêt': '💎 3D follow-up — album ready',
  '💎 Boostez la conversion : créez son album 3D': '💎 Boost the conversion: create their 3D album',
  "Ce client a envoyé ses photos mais n'a pas continué. Construisez l'album à sa place (éditeur → ✨ Régénérer IA → Commander), puis générez le lien d'aperçu (étape 4) — cette relance deviendra une invitation à feuilleter son album déjà prêt.": 'This customer sent their photos but did not continue. Build the album for them (editor → ✨ Regenerate AI → Order), then generate the preview link (step 4) — this follow-up will become an invitation to flip through their already-ready album.',
  "Ouvrir l'éditeur ↗": 'Open the editor ↗',
  'Carte Aperçu →': 'Preview card →',
  'Masquer le message': 'Hide the message',
  'Voir le message complet': 'See the full message',
  'Lien 3D': '3D link',
  'Lien': 'Link',
  'Prévisualiser & envoyer': 'Preview & send',
  'Pour': 'For',
  '💎 Relance 3D': '💎 3D follow-up',
  '✏️ MODIFIABLE': '✏️ EDITABLE',
  "Le message est libre — modifiez le texte avant l'envoi. Le lien reste cliquable côté client.": 'The message is free — edit the text before sending. The link stays clickable for the customer.',
  'Réinitialiser': 'Reset',
  'Envoyer sur WhatsApp →': 'Send on WhatsApp →',
  // ── Relief 3D price, per order (owner, 2026-09-29) ─────────────────────────
  'Couverture Relief 3D': 'Relief 3D cover',
  'Modifier le prix du Relief 3D': 'Edit the Relief 3D price',
  'Prix du Relief 3D (MAD)': 'Relief 3D price (MAD)',
  'Offert (0 MAD)': 'Free (0 MAD)',
  '0 = offert': '0 = free',
  'Le prix ne peut pas être négatif (0 = offert).': 'The price cannot be negative (0 = free).',
  'Enregistré · total {before} → {after} MAD': 'Saved · total {before} → {after} MAD',
  'Colis AMEEX déjà créé : cliquez « Modifier » sur la carte AMEEX puis « Enregistrer » pour lui envoyer le nouveau montant.':
    'AMEEX parcel already created: click « Edit » on the AMEEX card, then « Save », to send it the new amount.',
  'Total corrigé à la main ({total} MAD) : il prime. Cliquez d’abord « Rétablir le prix automatique » pour changer le prix du Relief 3D.':
    'Total corrected by hand ({total} MAD): it wins. Click « Restore automatic price » first to change the Relief 3D price.',
  'Rétablir le prix automatique': 'Restore automatic price',
  // A refused status change says why (2026-09-30).
  'Statut non modifié :': 'Status not changed:',
  // Top-bar shortcuts: « Scanner » and « Aperçu 3D » (parcel scanner, 2026-10-01).
  // (« Aperçu » → « Preview » is above, line 154.)
  'Scanner les colis': 'Scan parcels',
  'Aperçu 3D': '3D preview',
  'Ouvrir l’aperçu 3D du client dans un nouvel onglet': 'Open the customer’s 3D preview in a new tab',
  'Recherche du lien d’aperçu…': 'Looking for the preview link…',
  'Lien d’aperçu indisponible — rechargez la page.': 'Preview link unavailable — reload the page.',
  'Pas encore d’aperçu : les PDF ne sont pas générés (étape 3).': 'No preview yet: the PDFs are not generated (step 3).',
  'Pas encore de lien d’aperçu : créez-le à l’étape 4 « Aperçu client ».': 'No preview link yet: create it in step 4 « Customer preview ».',

  // ── Accepted options (the extras offered after approval, 2026-10-03) ──────
  'Options acceptées': 'Accepted options',
  'Livraison express et autres options, hors Relief 3D — déjà dans le prix.': 'Express delivery and other options, Relief 3D excluded — already in the price.',
  // ── Why the order exists (migration 220, 2026-10-06) ──────────────────────
  'Créée parce que': 'Created because',
  // ── Redo card at the top of the order column (2026-10-11) ──────────────────
  'Nouveau redo': 'New redo',
  // ── Options de la commande — tick = the client takes it (2026-10-14) ───────
  'Options de la commande': 'Order options',
  'Cocher = le client prend l’option : elle est ajoutée à la commande et son prix au total, tout de suite. Décocher la retire.':
    'Tick = the client takes the option: it is added to the order and its price to the total, right away. Untick removes it.',
  'Ajouté à la commande': 'Added to the order',
  'pris sur le site': 'bought on the website',
  'Laisser l’assistant le proposer': 'Let the assistant offer it',
  '✓ L’assistant peut le proposer (sans l’ajouter) — annuler': '✓ The assistant may offer it (not added) — undo',
  'L’assistant l’a proposé — en attente de la réponse.': 'The assistant offered it — waiting for the answer.',
  'Refusé ou retiré — l’assistant ne le reproposera pas.': 'Declined or removed — the assistant will not offer it again.',
  'Réservé aux personnes qui peuvent corriger le prix': 'Only for people who can correct the price',
  'L’option n’a pas pu être enregistrée — réessayez.': 'The option could not be saved — try again.',
  '« {titre} » ajouté : total {avant} → {apres} MAD.': '« {titre} » added: total {avant} → {apres} MAD.',
  '« {titre} » retiré : total {avant} → {apres} MAD.': '« {titre} » removed: total {avant} → {apres} MAD.',
  'Le colis AMEEX est déjà créé avec l’ancien montant : cliquez « Modifier » sur la carte AMEEX pour envoyer le nouveau.':
    'The AMEEX parcel was already created with the old amount: click « Modifier » on the AMEEX card to send the new one.',
  '⚠️ La ligne Shopify n’est pas ajoutée automatiquement : ajoutez-la à la main sur la commande Shopify.':
    '⚠️ The Shopify line is not added automatically: add it by hand on the Shopify order.',

  // ── Photo dedup (migration 267) ───────────────────────────────────────────
  '{n} doublons retirés': '{n} duplicates removed',
  '{n} photos similaires': '{n} similar photos',
  '{n} en double dans l’album': '{n} placed twice in the album',
  '{n} aussi dans un autre album': '{n} also in another album',
  'Doublons et photos similaires': 'Duplicates and similar photos',
  'Gardée': 'Kept',
  'Doublon masqué': 'Hidden duplicate',
  'Photo similaire (gardée dans le plateau)': 'Similar photo (kept in the tray)',
  'Deux fois dans l’album': 'Twice in the album',
  'Aussi dans un autre album': 'Also in another album',
  'Restaurer': 'Restore',
  "Retirer de l'album": 'Remove from the album',
};
export default m;
